import { useState, useCallback } from 'react';
import { User } from 'types';
import {
	userSettingsService,
	CreateUserRequest,
	UpdateUserRequest,
	GetUsersParams,
	GetUsersResponse,
} from 'core/API/userSettings';

interface UseUserCrudState {
	users: User[];
	selectedUser: User | null;
	loading: boolean;
	error: string | null;
	meta: {
		total: number;
		page: number;
		limit: number;
	};
}

interface UseUserCrudActions {
	// Data fetching
	fetchUsers: (params?: GetUsersParams) => Promise<void>;
	fetchUser: (userId: number) => Promise<User | null>;
	
	// CRUD operations
	createUser: (userData: CreateUserRequest) => Promise<User | null>;
	updateUser: (userData: UpdateUserRequest) => Promise<User | null>;
	deleteUser: (userId: number) => Promise<void>;
	toggleUserStatus: (userId: number, active: boolean) => Promise<void>;
	
	// State management
	setUsers: (users: User[]) => void;
	setSelectedUser: (user: User | null) => void;
	clearError: () => void;
	resetState: () => void;
}

export interface UseUserCrudReturn extends UseUserCrudState, UseUserCrudActions {}

const initialState: UseUserCrudState = {
	users: [],
	selectedUser: null,
	loading: false,
	error: null,
	meta: {
		total: 0,
		page: 1,
		limit: 50,
	},
};

export const useUserCrud = (): UseUserCrudReturn => {
	const [state, setState] = useState<UseUserCrudState>(initialState);

	const setLoading = useCallback((loading: boolean) => {
		setState(prev => ({ ...prev, loading }));
	}, []);

	const setError = useCallback((error: string | null) => {
		setState(prev => ({ ...prev, error }));
	}, []);

	const clearError = useCallback(() => {
		setState(prev => ({ ...prev, error: null }));
	}, []);

	const setUsers = useCallback((users: User[]) => {
		setState(prev => ({ ...prev, users }));
	}, []);

	const setSelectedUser = useCallback((user: User | null) => {
		setState(prev => ({ ...prev, selectedUser: user }));
	}, []);

	const resetState = useCallback(() => {
		setState(initialState);
	}, []);

	const fetchUsers = useCallback(async (params?: GetUsersParams) => {
		try {
			setLoading(true);
			setError(null);
			
			const response: GetUsersResponse = await userSettingsService.getUsers(params);

			const data = response.data;
			
			setState(prev => ({
				...prev,
				users: data,
				meta: response.meta,
				loading: false,
			}));
		} catch (err: any) {
			setError(err?.message || 'Failed to fetch users');
			setLoading(false);
		}
	}, [setLoading, setError]);

	const fetchUser = useCallback(async (userId: number): Promise<User | null> => {
		try {
			setLoading(true);
			setError(null);
			
			const user = await userSettingsService.getUser(userId);
			setSelectedUser(user);
			setLoading(false);
			return user;
		} catch (err: any) {
			setError(err.message || 'Failed to fetch user');
			setLoading(false);
			return null;
		}
	}, [setLoading, setError, setSelectedUser]);

	const createUser = useCallback(async (userData: CreateUserRequest): Promise<User | null> => {
		try {
			setLoading(true);
			setError(null);
			
			const newUser = await userSettingsService.createUser(userData);
			
			// Add the new user to the current list
			setState(prev => ({
				...prev,
				users: [...prev.users, newUser],
				meta: {
					...prev.meta,
					total: prev.meta.total + 1,
				},
				loading: false,
			}));
			
			return newUser;
		} catch (err: any) {
			setError(err.message || 'Failed to create user');
			setLoading(false);
			return null;
		}
	}, [setLoading, setError]);

	const updateUser = useCallback(async (userData: UpdateUserRequest): Promise<User | null> => {
		try {
			setLoading(true);
			setError(null);
			
			const updatedUser = await userSettingsService.updateUser(userData);
			
			// Update the user in the current list
			setState(prev => ({
				...prev,
				users: prev.users.map(user => 
					user.id === updatedUser.id ? updatedUser : user
				),
				selectedUser: prev.selectedUser?.id === updatedUser.id ? updatedUser : prev.selectedUser,
				loading: false,
			}));
			
			return updatedUser;
		} catch (err: any) {
			setError(err.message || 'Failed to update user');
			setLoading(false);
			return null;
		}
	}, [setLoading, setError]);

	const deleteUser = useCallback(async (userId: number): Promise<void> => {
		try {
			setLoading(true);
			setError(null);
			
			await userSettingsService.deleteUser(userId);
			
			// Remove the user from the current list
			setState(prev => ({
				...prev,
				users: prev.users.filter(user => user.id !== userId),
				selectedUser: prev.selectedUser?.id === userId ? null : prev.selectedUser,
				meta: {
					...prev.meta,
					total: prev.meta.total - 1,
				},
				loading: false,
			}));
		} catch (err: any) {
			setError(err.message || 'Failed to delete user');
			setLoading(false);
		}
	}, [setLoading, setError]);

	const toggleUserStatus = useCallback(async (userId: number, active: boolean): Promise<void> => {
		try {
			setLoading(true);
			setError(null);
			
			const updatedUser = await userSettingsService.toggleUserStatus(userId, active);
			
			// Update the user in the current list
			setState(prev => ({
				...prev,
				users: prev.users.map(user => 
					user.id === updatedUser.id ? updatedUser : user
				),
				selectedUser: prev.selectedUser?.id === updatedUser.id ? updatedUser : prev.selectedUser,
				loading: false,
			}));
		} catch (err: any) {
			setError(err.message || 'Failed to toggle user status');
			setLoading(false);
		}
	}, [setLoading, setError]);

	return {
		// State
		users: state.users,
		selectedUser: state.selectedUser,
		loading: state.loading,
		error: state.error,
		meta: state.meta,
		
		// Actions
		fetchUsers,
		fetchUser,
		createUser,
		updateUser,
		deleteUser,
		toggleUserStatus,
		setUsers,
		setSelectedUser,
		clearError,
		resetState,
	};
};
